import axios from 'axios';
import { getImageKitUrl } from '../utils/imageKitUrl';

// Smart API Configuration with Production Detection
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl !== undefined && envUrl !== null && envUrl !== 'http://localhost:5000' && envUrl !== 'http://127.0.0.1:5000') {
    return envUrl;
  }
  
  if (typeof window !== 'undefined' && window.location) {
    if (window.REACT_APP_API_URL !== undefined && window.REACT_APP_API_URL !== null) {
      return window.REACT_APP_API_URL;
    }
    
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalhost) {
      return ''; // Relative path for same-origin Firebase Hosting rewrites (/api/...)
    }
  }
  
  return envUrl !== undefined ? envUrl : 'http://localhost:5000';
};

const API_BASE_URL = getApiBaseUrl();

// Global Axios Interceptor to attach the token
axios.interceptors.request.use(
  (config) => {
    const token = typeof window !== 'undefined' ? (localStorage.getItem('token') || localStorage.getItem('adminToken')) : null;
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/auth/login`,
    REGISTER: `${API_BASE_URL}/api/auth/register`,
    PROFILE: `${API_BASE_URL}/api/auth/profile`,
  },
  ADMIN: {
    DASHBOARD_STATS: `${API_BASE_URL}/api/admin/dashboard/stats`,
    PRODUCTS: `${API_BASE_URL}/api/admin/products`,
    PRODUCT_BY_ID: (id) => `${API_BASE_URL}/api/admin/products/${id}`,
    ORDERS: `${API_BASE_URL}/api/admin/orders`,
    ORDER_BY_ID: (id) => `${API_BASE_URL}/api/admin/orders/${id}`,
    ORDER_STATUS: (id) => `${API_BASE_URL}/api/admin/orders/${id}/status`,
    USERS: `${API_BASE_URL}/api/admin/users`,
    USER_BY_ID: (id) => `${API_BASE_URL}/api/admin/users/${id}`,
    USER_BLOCK: (id) => `${API_BASE_URL}/api/admin/users/${id}/block`,
    SECTIONS: `${API_BASE_URL}/api/admin/sections`,
    SECTION_BY_ID: (id) => `${API_BASE_URL}/api/admin/sections/${id}`,
    SECTION_BY_KEY: (key) => `${API_BASE_URL}/api/admin/sections/${key}`,
    UPLOAD: `${API_BASE_URL}/api/admin/upload`,
  },
  CMS: {
    SETTINGS: `${API_BASE_URL}/api/admin/cms/settings`,
    UPLOAD: `${API_BASE_URL}/api/admin/cms/upload`,
    MEDIA: `${API_BASE_URL}/api/admin/cms/media`,
    MEDIA_DELETE: (filename) => `${API_BASE_URL}/api/admin/cms/media/${filename}`,
    SECTIONS_REORDER: `${API_BASE_URL}/api/admin/cms/sections/reorder`,
    SECTIONS_BY_TYPE: (type) => `${API_BASE_URL}/api/admin/cms/sections/type/${type}`,
    SECTION_BY_KEY: (key) => `${API_BASE_URL}/api/admin/cms/sections/key/${key}`,
    SECTIONS: `${API_BASE_URL}/api/admin/sections`,
    SECTION_BY_ID: (id) => `${API_BASE_URL}/api/admin/sections/${id}`,
  },
  PUBLIC: {
    SECTION_BY_KEY: (key) => `${API_BASE_URL}/api/sections/${key}`,
  },
  SHIPPING: {
    QUOTE: `${API_BASE_URL}/api/shipping/quote`,
    CONFIG: `${API_BASE_URL}/api/shipping/config`
  }
};

/**
 * Fast & Reliable Image URL Resolver with Instant Cache-Busting
 */
export const getImageUrl = (url, transformations = {}, updatedAt = null) => {
  if (!url) return '/img/placeholder.svg';
  
  // Handle array of images: take first element
  let target = Array.isArray(url) ? url[0] : url;
  if (!target) return '/img/placeholder.svg';

  // Handle objects like { url: '...' }
  const rawUrl = typeof target === 'string' ? target : (target.url || target.src || '');
  if (!rawUrl) return '/img/placeholder.svg';

  let normalizedUrl = rawUrl.replace(/\\/g, '/').trim();
  
  if (normalizedUrl.startsWith('data:') || normalizedUrl.startsWith('blob:')) {
    return normalizedUrl;
  }

  // 1. Route all external HTTP/HTTPS images (Firebase Storage, etc.) through ImageKit
  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    return getImageKitUrl(normalizedUrl, transformations, updatedAt);
  }

  // 2. Clean local or upload relative paths
  const cleanUrl = normalizedUrl.startsWith('/') ? normalizedUrl.substring(1) : normalizedUrl;

  if (cleanUrl.startsWith('uploads/')) {
    return getImageKitUrl(cleanUrl, transformations, updatedAt);
  }

  return getImageKitUrl(normalizedUrl, transformations, updatedAt);
};

export default API_BASE_URL;
